import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslate } from '../../../components/TranslationText';
import PenaltyCard from '../Components/PenaltyCard';
import { getDecisionById } from '../Api/disciplinaryDecisionsApi';
import moment from 'moment';

export default function DisciplinaryDecisionDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { translate } = useTranslate();
    const [decision, setDecision] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDetails = async () => {
            setLoading(true);
            try {
                const data = await getDecisionById(id);
                setDecision(data);
            } catch (error) {
                console.error("Error fetching decision details:", error);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchDetails();
        }
    }, [id]);

    if (loading) {
        return (
            <div className="container py-5 d-flex justify-content-center" style={{ minHeight: '60vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!decision) {
        return (
            <div className="container py-5 text-center" style={{ minHeight: '60vh' }}>
                <h3 className="text-muted">{translate('web.decisionNotFound', 'Decision not found')}</h3>
                <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>
                    {translate('web.goBack', 'Go Back')}
                </button>
            </div>
        );
    }

    return (
        <div className="container pt-3 pb-5">
            {/* Header Section */}
            <div className="mb-4">
                <h1 className="fw-bold mb-4 text-dark" style={{ fontSize: '2.5rem' }}>
                    {translate('web.disciplinaryDecision', 'Disciplinary Decision')}
                </h1>
                
                <div className="bg-white p-main border-15 d-flex flex-wrap gap-5 align-items-center">
                    <div>
                        <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>{translate('web.source', 'Source')}</div>
                        <div className="text-dark" style={{ fontSize: '15px' }}>{decision.source}</div>
                    </div>
                    <div>
                        <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>{translate('web.decisionDate', 'Decision Date')}</div>
                        <div className="text-dark" style={{ fontSize: '15px' }}>{moment(decision.decisionDate).format('DD MMM YYYY')}</div>
                    </div>
                    <div>
                        <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>{translate('web.reason', 'Reason')}</div>
                        {decision.reason === 'DOPING' ? (
                            <span className="badge rounded-1 px-3 py-1" style={{ backgroundColor: '#ffebee', color: '#d32f2f', border: '1px solid #ffcdd2', fontSize: '12px', fontWeight: '600' }}>
                                <i className="fa-solid fa-triangle-exclamation me-2"></i>
                                Doping
                            </span>
                        ) : (
                            <span className="badge bg-secondary rounded-1 px-3 py-1" style={{ fontSize: '12px', fontWeight: '600' }}>
                                {decision.reason}
                            </span>
                        )}
                    </div>
                    <div>
                        <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>{translate('web.country', 'Country')}</div>
                        <div className="text-dark" style={{ fontSize: '15px' }}>
                            <i className="fa-regular fa-flag me-2 text-muted"></i>
                            {decision.country || 'United Arab Emirates'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Information Cards Section */}
            <div className="row g-4 mb-4">
                {/* Horse/Handler Details */}
                <div className="col-md-6">
                    <div className="bg-white p-main border-15 border-0 h-100">
                        <h5 className="fw-bold mb-4 d-flex align-items-center gap-3 text-dark">
                            <i className="fa-solid fa-circle-info text-danger fs-5"></i>
                            {decision.horse ? translate('web.horseDetails', 'Horse Details') : translate('web.handlerDetails', 'Handler Details')}
                        </h5>
                        
                        <div className="mb-1">
                            <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>{translate('web.name', 'Name')}</div>
                            <div className="fw-bold text-dark" style={{ fontSize: '1.2rem' }}>
                                {decision.horse ? decision.horse.name : decision.handler?.name}
                            </div>
                        </div>

                        <hr className="mb-1 mt-2" style={{ borderColor: '#eaeaea' }} />

                        {decision.horse && (
                            <div className="row g-3 mt-0 pt-0">
                                <div className="col-6">
                                    <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>{translate('web.regNo', 'Reg No')}</div>
                                    <div className="text-dark" style={{ fontSize: '14px' }}>{decision.horse.regNo}</div>
                                </div>
                                <div className="col-6">
                                    <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>{translate('web.studbook', 'Studbook')}</div>
                                    <div className="text-dark" style={{ fontSize: '14px' }}>{decision.horse.studbook}</div>
                                </div>
                                <div className="col-6">
                                    <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>{translate('web.color', 'Color')}</div>
                                    <div className="text-dark" style={{ fontSize: '14px' }}>{decision.horse.color}</div>
                                </div>
                                <div className="col-6">
                                    <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>{translate('web.sex', 'Sex')}</div>
                                    <div className="text-dark" style={{ fontSize: '14px' }}>{decision.horse.sex || 'Mare'}</div>
                                </div>
                                <div className="col-6">
                                    <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>{translate('web.dob', 'DOB')}</div>
                                    <div className="text-dark" style={{ fontSize: '14px' }}>{moment(decision.horse.dob).format('DD MMM YYYY')}</div>
                                </div>
                                <div className="col-6">
                                    <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>{translate('web.owner', 'Owner')}</div>
                                    <div className="text-dark" style={{ fontSize: '14px' }}>{decision.horse.owner || 'EAHS Service'}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Show Information */}
                <div className="col-md-6">
                    <div className="bg-white p-main border-15 border-0 h-100">
                        <h5 className="fw-bold mb-4 d-flex align-items-center gap-3 text-dark">
                            <i className="fa-solid fa-trophy text-danger fs-5"></i>
                            {translate('web.showInformation', 'Show Information')}
                        </h5>
                        
                        <div className="mb-1">
                            <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>{translate('web.showName', 'Show Name')}</div>
                            <div className="fw-bold text-dark" style={{ fontSize: '1.1rem' }}>
                                {decision.show?.name || 'Katara International Arabian Horse Festival (KIAHF)'}
                            </div>
                        </div>

                        <hr className="mb-1 mt-2" style={{ borderColor: '#eaeaea' }} />

                        <div className="row g-3 mt-0 pt-0">
                            <div className="col-6">
                                <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>{translate('web.level', 'Level')}</div>
                                <div className="text-dark" style={{ fontSize: '14px' }}>Title show</div>
                            </div>
                            <div className="col-6">
                                <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>{translate('web.date', 'Date')}</div>
                                <div className="text-dark" style={{ fontSize: '14px' }}>{decision.show?.dates || '1-5 Feb 2024'}</div>
                            </div>
                            <div className="col-6">
                                <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>{translate('web.venue', 'Venue')}</div>
                                <div className="text-dark" style={{ fontSize: '14px' }}>Doha</div>
                            </div>
                            <div className="col-6">
                                <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 'bold' }}>{translate('web.country', 'Country')}</div>
                                <div className="text-dark" style={{ fontSize: '14px' }}><i className="fa-regular fa-location-dot me-1 text-muted"></i> Qatar</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Details Section */}
            <div className="row">
                <div className="col-12">
                    <div className="bg-white p-main border-15 border-0">
                        <h5 className="fw-bold mb-4 text-dark">
                            {translate('web.details', 'Details')}
                        </h5>
                        
                        <div className="text-dark" style={{ fontSize: '15px', lineHeight: '1.8' }}>
                            {(decision.penaltiesDetails || decision.penaltiesSummary) ? (
                                (decision.penaltiesDetails || decision.penaltiesSummary).split('\n').map((paragraph, idx) => {
                                    if (!paragraph.trim()) return <br key={idx} />;
                                    
                                    // Handle bullet points
                                    const isBullet = paragraph.trim().startsWith('•');
                                    let text = isBullet ? paragraph.trim().substring(1).trim() : paragraph;
                                    
                                    // Handle bold text (simple parse for this specific case if needed, or just render)
                                    const isBold = text.includes('If the fine is not paid');

                                    return (
                                        <p key={idx} className={`mb-2 ${isBullet ? 'ms-3 position-relative' : ''}`} style={isBold ? { fontWeight: 'bold' } : {}}>
                                            {isBullet && <span className="position-absolute" style={{ left: '-1rem' }}>•</span>}
                                            {text}
                                        </p>
                                    );
                                })
                            ) : (
                                <>
                                    <p className="mb-3">
                                        {translate('web.placeholderArticle1', 'ABU DHABI, 17th January, 2026 (WAM) -- His Highness Sheikh Mansour bin Zayed Al Nahyan, Vice President, Deputy Prime Minister and Chairman of the Presidential Court, today met with H.H. Sheikh Jassim bin Hamad Al Thani, the Personal Representative of H.H. the Emir of the sisterly State of Qatar, who is visiting the UAE on the sidelines of the Global Champions Arabians Tour- Abu Dhabi, organised by the Emirates Arabian Horse Society (EAHS) at the Abu Dhabi Equestrian Club, with the participation of an elite group of breeders, owners and horse beauty stables from around the world.')}
                                    </p>
                                    <p className="mb-3">
                                        {translate('web.placeholderArticle2', 'H.H. Sheikh Mansour bin Zayed and H.H. Sheikh Jassim bin Hamad Al Thani exchanged cordial talks reflecting the depth of the fraternal relations between the two countries and their peoples. Following the meeting, the two sides attended part of the activities of the second day of the Global Champions Arabians Tour Championship for the 2026 season, which is being held for the first time in Abu Dhabi and the Middle East.')}
                                    </p>
                                    <p className="mb-0">
                                        {translate('web.placeholderArticle3', 'The staging of the championship embodies the outcome of constructive and fruitful cooperation between the Emirates Arabian Horse Society and the Global Champions Arabians Tour, with the unified efforts and coordination contributing to the delivery of a fully professional model in terms of organisation, judging and technical standards, thereby enhancing the confidence of owners and breeders globally and significantly elevating the standing of Arabian horse beauty championships on the international stage.')}
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
